import { PageSeo } from "@/components/PageSeo";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, ShieldAlert, Mail, KeyRound, AlertTriangle } from "lucide-react";
const mbLearnLogo = '/mblearn-logo-HD.png';
import { signInSchema, SignInInput } from "@/lib/validations";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { InstallPWAButton } from "@/components/InstallPWAButton";
import { SafariOnlyBanner } from "@/components/SafariOnlyBanner";
import { InAppBrowserNotice } from "@/components/access/InAppBrowserNotice";
import { MassageBeachBackground } from "@/components/MassageBeachBackground";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { lovable } from "@/integrations/lovable/index";
import { AppleSignInButton } from "@/components/auth/AppleSignInButton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Normalize a password input for the common case of an institutional TEMP
// password (format XXXX-XXXX-XXXX from an unambiguous alphabet). We:
//   • trim whitespace,
//   • uppercase the value when it already looks like a TEMP code,
//   • accept the dashless variant (12 chars) by re-inserting dashes.
// We never modify a normal user-set password (which may contain lowercase or
// arbitrary characters).
function normalizePasswordInput(raw: string): string {
  const trimmed = (raw || "").trim();
  if (!trimmed) return trimmed;
  const dashed = /^[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}$/;
  const dashless = /^[A-Za-z0-9]{12}$/;
  const tempAlphabet = /^[ABCDEFGHJKMNPQRSTUVWXYZ23456789]{12}$/;
  if (dashed.test(trimmed)) return trimmed.toUpperCase();
  if (dashless.test(trimmed)) {
    const u = trimmed.toUpperCase();
    if (tempAlphabet.test(u)) {
      return `${u.slice(0, 4)}-${u.slice(4, 8)}-${u.slice(8, 12)}`;
    }
  }
  return trimmed;
}


const Login = () => {
  const { t, language } = useLanguage();
  const { user, session, loading: authLoading, signIn } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<SignInInput>({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState<Partial<SignInInput>>({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(5);
  const [signInFailed, setSignInFailed] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestEmail, setRequestEmail] = useState("");
  const [requestSending, setRequestSending] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && user && session && user.email_confirmed_at) {
      console.log('🔄 [Login] User already authenticated, redirecting to dashboard');
      navigate('/dashboard', { replace: true });
    }
  }, [user, session, authLoading, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Live-trim email to avoid silent leading/trailing spaces.
    const next = name === "email" ? value.replace(/^\s+|\s+$/g, "") : value;
    setFormData({ ...formData, [name]: next });
    setErrors({ ...errors, [name]: undefined });
    if (signInFailed) setSignInFailed(false);
  };

  const openRequestDialog = () => {
    setRequestEmail(formData.email || "");
    setRequestSent(false);
    setRequestOpen(true);
  };

  const submitAccessRequest = async () => {
    setRequestSending(true);
    try {
      await supabase.functions.invoke('request-student-access', {
        body: { email: (requestEmail || "").trim().toLowerCase() },
      });
      // The endpoint always returns a generic response (no enumeration).
      setRequestSent(true);
    } catch (err) {
      console.warn('[Login] request-student-access failed (showing generic ok):', err);
      setRequestSent(true);
    } finally {
      setRequestSending(false);
    }
  };

  /** Helper: wrap a promise with a timeout (rejects after ms) */
  const withTimeout = <T,>(promise: Promise<T>, ms: number): Promise<T> =>
    Promise.race([
      promise,
      new Promise<T>((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), ms)
      ),
    ]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    setSignInFailed(false);

    // Normalize before validation: trim email, normalize TEMP-shaped passwords.
    const normalized: SignInInput = {
      email: (formData.email || "").trim().toLowerCase(),
      password: normalizePasswordInput(formData.password || ""),
    };
    // Keep what we send and what the user sees in sync.
    if (normalized.email !== formData.email || normalized.password !== formData.password) {
      setFormData(normalized);
    }

    // Validate
    const result = signInSchema.safeParse(normalized);
    if (!result.success) {
      const fieldErrors: Partial<SignInInput> = {};
      result.error.issues.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as keyof SignInInput] = err.message as any;
        }
      });
      setErrors(fieldErrors);
      setLoading(false);
      return;
    }


    try {
      // PERF (P0): we no longer call an external IP echo service before login.
      // It blocked the critical path for up to 3s on mobile, and per our
      // privacy rules we do not store raw client IPs anyway. The server-side
      // functions derive/hash the IP themselves from request headers.
      const clientIp = null;

      // Check rate limit before attempting login (2s cap — advisory only)
      let rateLimitCheck: any = null;
      try {
        const { data, error: rateLimitError } = await withTimeout(
          supabase.functions.invoke('check-rate-limit', {
            body: { 
              email: normalized.email,
              ipAddress: clientIp
            }
          }),
          2000
        );

        if (rateLimitError) {
          console.error('Rate limit check error:', rateLimitError);
        } else {
          rateLimitCheck = data;
        }
      } catch (err) {
        console.warn('Rate limit check timed out, proceeding with login');
      }

      if (rateLimitCheck?.isRateLimited) {
        setRateLimited(true);
        setAttemptsLeft(0);
        const reason = rateLimitCheck.reason || t("auth.rateLimitExceeded");
        toast.error(reason, {
          description: t("auth.tryAgainLater")
        });
        setLoading(false);
        return;
      }

      if (rateLimitCheck?.failedAttempts) {
        setAttemptsLeft(5 - rateLimitCheck.failedAttempts);
      }

      // Attempt login (this is the critical path — 15s timeout)
      const { error } = await withTimeout(
        signIn(normalized.email, normalized.password),
        15000
      );

      // Record login attempt — fire-and-forget, don't block UI
      supabase.functions.invoke('record-login-attempt', {
        body: {
          email: normalized.email,
          success: !error,
          ipAddress: clientIp
        }
      }).catch(err => console.warn('record-login-attempt failed:', err));

      if (error) {
        setSignInFailed(true);
      } else {
        // Navigate IMMEDIATELY on success. `signIn` already resolved after
        // auth + device-session registration, so there is nothing left to
        // wait for. The onAuthStateChange listener stays as a synchronizer
        // (it is a no-op once we are off /auth/login), and the delayed check
        // below only acts as a last-resort fallback for WebViews that swallow
        // the first navigation — it self-cancels when we already navigated.
        const returnTo = new URLSearchParams(window.location.search).get('returnTo');
        const target =
          returnTo && !returnTo.startsWith('/auth/') && returnTo !== '/'
            ? returnTo
            : '/dashboard';
        navigate(target, { replace: true });

        setTimeout(() => {
          if (window.location.pathname.startsWith('/auth/login')) {
            console.log('🛟 [Login] Fallback navigation after successful sign-in →', target);
            navigate(target, { replace: true });
          }
        }, 1200);
      }



      if (error && rateLimitCheck) {
        const newAttemptsLeft = 5 - (rateLimitCheck.failedAttempts + 1);
        setAttemptsLeft(newAttemptsLeft);

        if (newAttemptsLeft <= 0) {
          setRateLimited(true);
          toast.error(t("auth.accountTemporarilyLocked"), {
            description: t("auth.tryAgainLater")
          });
        } else if (newAttemptsLeft <= 2) {
          toast.warning(t("auth.attemptsWarning"), {
            description: `${newAttemptsLeft} ${t("auth.attemptsRemaining")}`
          });
        }
      }
    } catch (error: any) {
      console.error('Login error:', error);
      if (error?.message === 'timeout') {
        toast.error(t("auth.connectionError") || "Error de conexión. Intenta de nuevo.");
      } else {
        toast.error(t("auth.errorSigningIn"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen relative flex items-center justify-center p-4 overflow-x-hidden bg-background"
      style={{
        paddingTop: "max(1rem, env(safe-area-inset-top))",
        paddingBottom: "max(1rem, env(safe-area-inset-bottom))",
      }}
    >

      <PageSeo
        path="/auth/login"
        title="Log in to MBLearn — MBLEx Practice Test & Study App"
        description="Sign in to MBLearn to continue your MBLEx practice tests, study modules and real-exam simulations."
        noindex
      />
      <SafariOnlyBanner />
      {/* Massage beach background */}
      <MassageBeachBackground />
      
      <div className="mobile-container w-full max-w-[430px] p-4 sm:p-8 relative z-10 animate-fade-in">
        <div className="mb-4 text-center">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
            ← {language === "es" ? "Volver al inicio" : "Back to home"}
          </Link>
        </div>
        <div className="text-center mb-8 animate-scale-in">
          <img src={mbLearnLogo} alt="MBLearn" className="w-28 h-28 rounded-2xl mx-auto mb-4 shadow-lg object-contain bg-transparent" />


          <h1 className="text-3xl font-bold text-foreground">
            {t("auth.login")}
          </h1>
          <p className="text-muted-foreground mt-2">{t("auth.continuePrep")}</p>
          {/* QA marker — verify Android receives fresh frontend after kill-switch deploy. Remove after validation. */}
          <p className="text-[10px] text-muted-foreground/60 mt-1" aria-hidden="true">v✓ actualizado 2</p>
        </div>

        {rateLimited && (
          <Alert variant="destructive" className="mb-4">
            <ShieldAlert className="h-4 w-4" />
            <AlertDescription className="space-y-2">
              <p>{t("auth.accountTemporarilyLocked")}</p>
              <p className="text-sm">
                Mientras esperas 15 minutos, puedes restablecer tu contraseña o solicitar un nuevo acceso.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => navigate('/auth/forgot')}
                >
                  <KeyRound className="h-4 w-4" /> Restablecer contraseña
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={openRequestDialog}
                >
                  <Mail className="h-4 w-4" /> Pedir nuevo acceso
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <InAppBrowserNotice />

        <form onSubmit={handleSubmit} className="space-y-4 bg-card/80 backdrop-blur-md p-6 rounded-xl shadow-2xl border border-border animate-fade-in" style={{ animationDelay: "0.1s", animationFillMode: "backwards" }}>
          <div>
            <Label htmlFor="email" className="text-foreground">{t("auth.email")}</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              className={`bg-background text-foreground border-input placeholder:text-muted-foreground ${errors.email ? "border-destructive" : ""}`}
            />
            {errors.email && (
              <p className="text-sm text-destructive mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <Label htmlFor="password" className="text-foreground">{t("auth.password")}</Label>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleChange}
                className={`bg-background text-foreground border-input placeholder:text-muted-foreground pr-10 ${errors.password ? "border-destructive" : ""}`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && (
              <p className="text-sm text-destructive mt-1">{errors.password}</p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <Link
              to="/auth/forgot"
              className="text-sm text-muted-foreground hover:text-foreground hover:underline"
            >
              {t("auth.forgotPassword")}
            </Link>
            <Link
              to="/auth/student-access"
              className="text-sm text-primary hover:underline font-medium"
            >
              Acceso con código
            </Link>
          </div>

          <Button
            type="submit"
            className="w-full py-6 bg-gradient-to-r from-primary to-accent text-primary-foreground"
            disabled={loading || rateLimited}
            size="lg"
          >
            {loading ? t("auth.signingIn") : rateLimited ? t("auth.accountLocked") : t("auth.login")}
          </Button>

          <p className="text-xs text-muted-foreground text-center mt-2">
            {t("auth.singleDeviceNotice")}
          </p>

          {attemptsLeft < 5 && attemptsLeft > 0 && !rateLimited && (
            <p className="text-sm text-amber-600 dark:text-amber-400 text-center">
              {attemptsLeft} {t("auth.attemptsRemaining")}
            </p>
          )}

          {signInFailed && !rateLimited && (
            <Alert className="mt-2 border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <AlertDescription className="text-sm text-amber-900 dark:text-amber-100 space-y-2">
                <p>No pudimos ingresar.</p>
                <p>Verifica tu correo y contraseña.</p>
                <p>Si usas una contraseña temporal, cópiala exactamente como aparece en tu correo.</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => navigate('/auth/forgot')}
                  >
                    <KeyRound className="h-4 w-4" /> Restablecer contraseña
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={openRequestDialog}
                  >
                    <Mail className="h-4 w-4" /> Pedir nuevo acceso
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}
        </form>

        <Dialog open={requestOpen} onOpenChange={setRequestOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Pedir nuevo acceso</DialogTitle>
              <DialogDescription>
                Si tu institución te inscribió en MBLearn y no puedes ingresar,
                te enviaremos un nuevo correo con instrucciones. Por seguridad,
                la respuesta es la misma exista o no la cuenta.
              </DialogDescription>
            </DialogHeader>

            {!requestSent ? (
              <div className="space-y-3">
                <Label htmlFor="request-email">Tu correo</Label>
                <Input
                  id="request-email"
                  type="email"
                  value={requestEmail}
                  onChange={(e) => setRequestEmail(e.target.value)}
                  placeholder="tu@correo.com"
                  autoComplete="email"
                />
                <DialogFooter>
                  <Button
                    variant="ghost"
                    onClick={() => setRequestOpen(false)}
                    disabled={requestSending}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={submitAccessRequest}
                    disabled={requestSending || !requestEmail.trim()}
                  >
                    {requestSending ? 'Enviando…' : 'Enviar'}
                  </Button>
                </DialogFooter>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Si tu correo está registrado con una institución, recibirás
                  un email con instrucciones. Revisa tu bandeja y la carpeta de
                  spam.
                </p>
                <DialogFooter>
                  <Button onClick={() => setRequestOpen(false)}>Entendido</Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>


        {/* Apple Sign In oculto temporalmente — ver ticket Apple BYOC */}
        {false && (
          <div className="mt-4 animate-fade-in" style={{ animationDelay: "0.15s", animationFillMode: "backwards" }}>
            <div className="relative flex items-center justify-center mb-4">
              <div className="border-t border-border flex-grow" />
              <span className="mx-3 text-xs text-muted-foreground uppercase">{t("auth.orContinueWith")}</span>
              <div className="border-t border-border flex-grow" />
            </div>
            <AppleSignInButton />
          </div>
        )}

        <div className="mt-6 text-center text-sm animate-fade-in" style={{ animationDelay: "0.2s", animationFillMode: "backwards" }}>
          <p className="text-muted-foreground">
            {t("auth.noAccount")}{" "}
            <Link to="/auth/register" className="text-primary hover:underline font-medium">
              {t("auth.registerNow")}
            </Link>
          </p>
          <p className="mt-3 text-xs text-muted-foreground">
            <Link to="/schools" className="hover:text-primary hover:underline">
              {language === "es"
                ? "¿Eres una escuela? Portal institucional"
                : "Are you a school? Institutional portal"}
            </Link>
          </p>
        </div>

      </div>
    </div>
  );
};
export default Login;
